import { createAction, props } from '@ngrx/store';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite,
  AttachmentAttachmentRead,
  DrainageSurfaceCharacteristicAppEntityDrainageSurfaceCharacteristicWrite,
  EntityTypeAppEntityDictionaryEntityType,
  InvestorRepresentativeAppEntityInvestorRepresentativeWrite,
  LargeFamilyCardAppEntityLargeFamilyCardWrite,
  ParcelAppEntityParcelWrite,
  PatchApplicantApplicantItemRequestParams,
  PatchContractorContractorItemRequestParams,
  RoadOccupationTypeAppEntityDictionaryRoadOccupationType,
  ShipmentAppEntityShipmentRead,
  TreeRemovalReasonAppEntityDictionaryTreeRemovalReason,
  TreeToRemoveAppEntityTreeToRemoveWrite
} from '../../openapi';
import { ApplicationCode } from '../../services/application.common.service';
import { ApplicationListItem, ApplicationPayment, AttachmentPositions } from '../../services/application.common.model';
import {
  RoadOccupationFormValue
} from '../../containers/applications/application/common/occupation-data-section/forms/road-occupation.form';
import { HttpErrorResponse } from '@angular/common/http';

export const createApplication = createAction(
  '[Application] create application',
  props<{
    serviceTypeId: string;
    application?: ApplicationAppEntityApplicationWrite;
    applicants: PatchApplicantApplicantItemRequestParams[];
    successFnc: (applicationId: string) => void;
    failureFnc: () => void;
  }>()
);

export const createApplicationSuccess = createAction(
  '[Application] create application success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const createApplicationFailure = createAction('[Application] create application failure');

export const setApplication = createAction('[Application] set application', props<{
  application: ApplicationAppEntityApplicationRead | null
}>());

export const loadApplication = createAction('[Application] load application', props<{ id: string }>());

export const loadApplicationSuccess = createAction(
  '[Application] load application success',
  props<{ application: ApplicationAppEntityApplicationRead | null }>()
);

export const loadApplicationFailure = createAction(
  '[Application] load application failure'
);

export const updateApplication = createAction(
  '[Application] update application data',
  props<{
    id: string;
    application: ApplicationAppEntityApplicationWrite;
    successFnc: (applicationId: string) => void;
    failureFnc: () => void;
  }>()
);

export const updateApplicationSuccess = createAction(
  '[Application] update application data success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const updateApplicationFailure = createAction(
  '[Application] update application data failure'
);

export const updateParcels = createAction(
  '[Application] update parcels',
  props<{
    currentApp: ApplicationAppEntityApplicationRead;
    payload: ApplicationAppEntityApplicationWrite;
    parcels: ParcelAppEntityParcelWrite[];
    successFnc: (applicationId: string) => void;
    failureFnc: () => void;
    tag?: string;
  }>()
);

export const updateParcelsSuccess = createAction(
  '[Application] update parcels success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const updateParcelsFailure = createAction(
  '[Application] update parcels failure'
);

export const updateLargeFamilyCards = createAction(
  '[Application] update large family cards',
  props<{
    currentApp: ApplicationAppEntityApplicationRead;
    payload: ApplicationAppEntityApplicationWrite;
    cards: LargeFamilyCardAppEntityLargeFamilyCardWrite[];
    successFnc: (applicationId: string) => void;
    failureFnc: () => void;
  }>()
);

export const updateLargeFamilyCardsSuccess = createAction(
  '[Application] update large family cards success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const updateLargeFamilyCardsFailure = createAction(
  '[Application] update large family cards failure'
);

export const updateApplicants = createAction(
  '[Application] update applicants',
  props<{
    applicationId: string;
    applicants: PatchApplicantApplicantItemRequestParams[];
    application?: ApplicationAppEntityApplicationWrite;
    successFnc: (applicationId: string) => void;
    failureFnc: () => void;
  }>()
);

export const updateApplicantsSuccess = createAction(
  '[Application] update applicants success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const updateApplicantsFailure = createAction('[Application] update applicants failure');

export const updateContractors = createAction(
  '[Application] update contractors',
  props<{
    applicationId: string;
    contractors: PatchContractorContractorItemRequestParams[];
    successFnc: (applicationId: string) => void;
    failureFnc: () => void;
  }>()
);

export const updateContractorsSuccess = createAction(
  '[Application] update contractors success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const updateContractorsFailure = createAction('[Application] update contractors failure');

export const updateOccupations = createAction(
  '[Application] update occupations',
  props<{
    application: ApplicationAppEntityApplicationRead | null,
    occupations: RoadOccupationFormValue[],
    successFnc: (applicationId: string) => void,
    failureFnc: () => void
  }>()
);

export const updateOccupationsSuccess = createAction(
  '[Application] update occupations success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const updateOccupationsFailure = createAction(
  '[Application] update occupations failure'
);

export const updateApplicationAttachments = createAction(
  '[Application] update application attachments',
  props<{
    application: ApplicationAppEntityApplicationRead,
    attachments: AttachmentAttachmentRead[],
    successFnc: (applicationId: string) => void,
    failureFnc: () => void
  }>()
);

export const updateApplicationAttachmentsSuccess = createAction(
  '[Application] update application attachments success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const updateApplicationAttachmentsFailure = createAction(
  '[Application] update application attachments failure'
);

export const loadApplicationList = createAction(
  '[Application] load application list',
  props<{ code: ApplicationCode }>()
);

export const loadApplicationListSuccess = createAction(
  '[Application] load application list success',
  props<{ applicationList: ApplicationListItem[] }>()
);

export const loadApplicationListFailure = createAction(
  '[Application] load application list failure'
);

export const approveApplication = createAction(
  '[Application] approve',
  props<{
    applicationId: string,
    successFnc: () => void,
    failureFnc: (e: HttpErrorResponse) => void
  }>()
);

export const approveApplicationSuccess = createAction(
  '[Application] approve success'
);

export const approveApplicationFailure = createAction(
  '[Application] approve failure'
);

export const loadRoadOccupationTypes = createAction('[Application] load road occupation types');

export const loadRoadOccupationTypesSuccess = createAction(
  '[Application] load road occupation types success',
  props<{ roadOccupationTypes: RoadOccupationTypeAppEntityDictionaryRoadOccupationType[] }>()
);

export const loadRoadOccupationTypesFailure = createAction('[Application] load road occupation types failure');

export const setPayments = createAction(
  '[Application] set payments',
  props<{ applicationPayment: ApplicationPayment | null }>()
);

export const loadPayments = createAction(
  '[Application] load payments',
  props<{ applicationId: string }>()
);

export const loadPaymentsSuccess = createAction(
  '[Application] load payments success',
  props<{ applicationPayment: ApplicationPayment | null }>()
);

export const loadPaymentsFailure = createAction('[Application] load payments failure');

export const sendApplication = createAction('[Application] send',
  props<{
    applicationId: string;
    successFnc: () => void;
    failureFnc: () => void;
  }>()
);

export const sendApplicationSuccess = createAction(
  '[Application] send application success'
);

export const sendApplicationFailure = createAction('[Application] send application failure');

export const signApplication = createAction(
  '[Application] sign application',
  props<{
    applicationId: string;
    successFnc: (redirectUrl: string) => void;
    failureFnc: () => void;
  }>()
);

export const signApplicationSuccess = createAction('[Application] sign application success');

export const signApplicationFailure = createAction('[Application] sign application failure');

export const fakeSignApplication = createAction('[Application] fake sign application', props<{
    applicationId: string;
    successFnc: () => void;
    failureFnc: () => void;
  }>()
);

export const fakeSignApplicationSuccess = createAction(
  '[Application] fake sign application success'
);

export const fakeSignApplicationFailure = createAction('[Application] fake sign application failure');

export const setShipments = createAction('[Application] set shipments', props<{
  shipments: ShipmentAppEntityShipmentRead[]
}>());

export const loadShipments = createAction(
  '[Application] load shipments',
  props<{ applicationId: string }>()
);

export const loadShipmentsSuccess = createAction(
  '[Application] load shipments success',
  props<{ shipments: ShipmentAppEntityShipmentRead[] }>()
);

export const loadShipmentsFailure = createAction(
  '[Application] load shipments failure'
);

export const loadShipment = createAction(
  '[Application] load shipment',
  props<{
    id: string
  }>()
);

export const loadShipmentSuccess = createAction(
  '[Application] load shipment success',
  props<{
    shipment: ShipmentAppEntityShipmentRead | null
  }>()
);

export const loadShipmentFailure = createAction(
  '[Application] load shipment failure'
);

export const loadTreeRemovalReasons = createAction('[Application] load tree removal reasons');

export const loadTreeRemovalReasonsSuccess = createAction(
  '[Application] load tree removal reasons success',
  props<{
    treeRemovalReasons: TreeRemovalReasonAppEntityDictionaryTreeRemovalReason[]
  }>()
);

export const loadTreeRemovalReasonsFailure = createAction(
  '[Application] load tree removal reasons failure'
);

export const updateTreeCuttingInfo = createAction(
  '[Application] update tree cutting info',
  props<{
    applicationId: string;
    application: ApplicationAppEntityApplicationWrite;
    treesToRemove: TreeToRemoveAppEntityTreeToRemoveWrite[];
    successFnc: (applicationId: string) => void;
    failureFnc: () => void;
  }>()
);

export const updateTreeCuttingInfoSuccess = createAction(
  '[Application] update tree cutting info success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const updateTreeCuttingInfoFailure = createAction(
  '[Application] update tree cutting info failure'
);

export const updateInvestorRepresentatives = createAction('[Application] update investor representatives', props<{
  currentApp: ApplicationAppEntityApplicationRead,
  payload: ApplicationAppEntityApplicationWrite,
  investorRepresentatives: InvestorRepresentativeAppEntityInvestorRepresentativeWrite[];
  successFnc: (applicationId: string) => void;
  failureFnc: () => void;
}>());

export const updateInvestorRepresentativesSuccess = createAction(
  '[Application] update investor representatives success',
  props<{ application: ApplicationAppEntityApplicationRead }>()
);

export const updateInvestorRepresentativesFailure = createAction('[Application] update investor representatives failure');

export const loadEntityTypes = createAction('[Application] load entity types');

export const loadEntityTypesSuccess = createAction(
  '[Application] load entity types success',
  props<{
    entityTypes: EntityTypeAppEntityDictionaryEntityType[]
  }>()
);

export const loadEntityTypesFailure = createAction('[Application] load entity types failure');

export const updateAssureReceptionWater = createAction(
  '[Application] update assure reception water',
  props<{
    id: string,
    application: ApplicationAppEntityApplicationRead,
    payload: ApplicationAppEntityApplicationWrite,
    drainageSurfaceCharacteristics: DrainageSurfaceCharacteristicAppEntityDrainageSurfaceCharacteristicWrite[],
    successFnc: (applicationId: string) => void;
    failureFnc: () => void;
  }>()
);

export const updateAssureReceptionWaterSuccess = createAction(
  '[Application] update assure reception water success',
  props<{
    application: ApplicationAppEntityApplicationRead
  }>()
);

export const updateAssureReceptionWaterFailure = createAction('[Application] update assure reception water failure');

export const loadServiceAttachmentPositions = createAction(
  '[Application] load service attachment positions',
  props<{
    appId: string
  }>()
);

export const loadServiceAttachmentPositionsSuccess = createAction(
  '[Application] load service attachment positions success',
  props<{
    serviceAttachmentPositions: AttachmentPositions
  }>()
);

export const loadServiceAttachmentPositionsFailure = createAction(
  '[Application] load service attachment positions failure'
);
